import { useState, useMemo } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, ChevronRight, Search } from 'lucide-react';
import { SchemaField } from '@/lib/data-standards/schemas';

interface FieldSelectorProps {
  fields: SchemaField[];
  schema: object;
  selectedFields: string[];
  onSelectionChange: (fields: string[]) => void;
  requiredOverrides: Record<string, boolean>;
  onRequiredChange: (field: string, required: boolean) => void;
}

export function FieldSelector({
  fields,
  schema,
  selectedFields,
  onSelectionChange,
  requiredOverrides,
  onRequiredChange,
}: FieldSelectorProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [requiredOpen, setRequiredOpen] = useState(true);
  const [optionalOpen, setOptionalOpen] = useState(true);

  const schemaObj = schema as Record<string, unknown>;
  const schemaRequired = (schemaObj.required as string[]) || [];

  const { requiredFields, optionalFields } = useMemo(() => {
    const required: SchemaField[] = [];
    const optional: SchemaField[] = [];

    fields.forEach(field => {
      const isRequired = schemaRequired.includes(field.name);
      if (isRequired) {
        required.push(field);
      } else {
        optional.push(field);
      }
    });

    return { requiredFields: required, optionalFields: optional };
  }, [fields, schemaRequired]);

  const filteredRequired = useMemo(() => {
    if (!searchQuery) return requiredFields;
    const query = searchQuery.toLowerCase();
    return requiredFields.filter(
      f => f.name.toLowerCase().includes(query) || f.description?.toLowerCase().includes(query)
    );
  }, [requiredFields, searchQuery]);

  const filteredOptional = useMemo(() => {
    if (!searchQuery) return optionalFields;
    const query = searchQuery.toLowerCase();
    return optionalFields.filter(
      f => f.name.toLowerCase().includes(query) || f.description?.toLowerCase().includes(query)
    );
  }, [optionalFields, searchQuery]);

  const handleSelectAll = () => {
    onSelectionChange(fields.map(f => f.name));
  };

  const handleDeselectAll = () => {
    onSelectionChange([]);
  };

  const handleFieldToggle = (fieldName: string, checked: boolean) => {
    if (checked) {
      onSelectionChange([...selectedFields, fieldName]);
    } else {
      onSelectionChange(selectedFields.filter(f => f !== fieldName));
    }
  };

  const getEffectiveRequired = (fieldName: string) => {
    if (fieldName in requiredOverrides) {
      return requiredOverrides[fieldName];
    }
    return schemaRequired.includes(fieldName);
  };

  const renderFieldRow = (field: SchemaField) => {
    const isSelected = selectedFields.includes(field.name);
    const isRequired = getEffectiveRequired(field.name);
    const originalRequired = schemaRequired.includes(field.name);
    const hasOverride = field.name in requiredOverrides;

    return (
      <div
        key={field.name}
        className={`p-3 border rounded-lg transition-colors ${
          isSelected ? 'border-primary/50 bg-primary/5' : 'border-border bg-card'
        }`}
      >
        <div className="flex items-start gap-3">
          <Checkbox
            id={`field-${field.name}`}
            checked={isSelected}
            onCheckedChange={(checked) => handleFieldToggle(field.name, checked as boolean)}
            className="mt-1"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <label
                htmlFor={`field-${field.name}`}
                className="font-mono text-sm font-medium cursor-pointer"
              >
                {field.name}
              </label>
              <Badge variant="outline" className="text-xs">
                {field.type}
              </Badge>
              {hasOverride && (
                <Badge variant="secondary" className="text-xs">
                  modified
                </Badge>
              )}
            </div>
            {field.description && (
              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                {field.description}
              </p>
            )}
            {isSelected && (
              <div className="flex items-center gap-2 mt-2 pt-2 border-t border-border/50">
                <span className="text-xs text-muted-foreground">Required:</span>
                <Switch
                  checked={isRequired}
                  onCheckedChange={(checked) => onRequiredChange(field.name, checked)}
                  className="scale-75"
                />
                <span className="text-xs text-muted-foreground">
                  {isRequired ? 'Yes' : 'No'}
                  {!hasOverride && originalRequired && ' (default)'}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 p-4 border-b">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search fields..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleSelectAll}>
            Select All
          </Button>
          <Button variant="outline" size="sm" onClick={handleDeselectAll}>
            Deselect All
          </Button>
        </div>
      </div>

      <div className="px-4 py-2 text-sm text-muted-foreground border-b">
        {selectedFields.length} of {fields.length} fields selected
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4">
          {/* Required Fields Section */}
          <Collapsible open={requiredOpen} onOpenChange={setRequiredOpen}>
            <CollapsibleTrigger className="flex items-center gap-2 w-full text-left font-medium text-sm hover:text-primary transition-colors">
              {requiredOpen ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
              Required Fields ({filteredRequired.length})
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2 space-y-2">
              {filteredRequired.length > 0 ? (
                filteredRequired.map(renderFieldRow)
              ) : (
                <p className="text-sm text-muted-foreground py-2">
                  {searchQuery ? 'No required fields match your search.' : 'No required fields.'}
                </p>
              )}
            </CollapsibleContent>
          </Collapsible>

          {/* Optional Fields Section */}
          <Collapsible open={optionalOpen} onOpenChange={setOptionalOpen}>
            <CollapsibleTrigger className="flex items-center gap-2 w-full text-left font-medium text-sm hover:text-primary transition-colors">
              {optionalOpen ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
              Optional Fields ({filteredOptional.length})
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2 space-y-2">
              {filteredOptional.length > 0 ? (
                filteredOptional.map(renderFieldRow)
              ) : (
                <p className="text-sm text-muted-foreground py-2">
                  {searchQuery ? 'No optional fields match your search.' : 'No optional fields.'}
                </p>
              )}
            </CollapsibleContent>
          </Collapsible>
        </div>
      </ScrollArea>
    </div>
  );
}
